import { axiosInstance } from "../services/v2/axiosInstance";

/*
	WAPP instalable en el celular y avisos aunque esté cerrada (Esteban, 2026-10-09: "para que les lleguen
	notificaciones a las chicas", como con Zoho Cliq).

	- Service worker /sw.js: solo avisos; no cachea nada (ver public/sw.js). Se registra al arrancar (index.tsx).
	- Avisos = Web Push estándar con llaves VAPID: el navegador da una suscripción, el back la guarda
	  (POST /app/_suscripcion) y manda los avisos cifrados (App\Services\AvisosApp).
	- Presencia: mientras WAPP está a la vista y la usan, se avisa al back cada 3 min (POST /app/_presencia; 45 s hasta el 16-oct-2026) para que no
	  mande push a quien ya lo está viendo en pantalla.
	- iPhone: los avisos solo existen con WAPP instalada (Safari › Compartir › Agregar a inicio, iOS 16.4 o más nuevo).
*/

const DISP_KEY = "wapp.dispositivo";
const API = (process.env.REACT_APP_API_URL_V2 || "").replace(/\/$/, "");

export const esIOS = (): boolean =>
	/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
export const esAndroid = (): boolean => /Android/i.test(navigator.userAgent);
/** Safari de verdad en iPhone (Chrome/Firefox/Edge en iPhone también dicen "Safari" en el userAgent). */
export const esSafariIOS = (): boolean => esIOS() && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(navigator.userAgent);
export const plataforma = (): "ios" | "android" | "escritorio" => (esIOS() ? "ios" : esAndroid() ? "android" : "escritorio");
export const esCelular = (): boolean => esIOS() || esAndroid();

/** ¿Se abrió desde el ícono (app instalada) y no desde el navegador? */
export const instalada = (): boolean => {
	try {
		return window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;
	} catch {
		return false;
	}
};

export const pushSoportado = (): boolean =>
	typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const dispositivoId = (): string => {
	try {
		let d = localStorage.getItem(DISP_KEY);
		if (!d) {
			d = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
			localStorage.setItem(DISP_KEY, d);
		}
		return d;
	} catch {
		return "sin-almacenamiento";
	}
};

// ------------------------------------------------------------------ service worker

let registro: Promise<ServiceWorkerRegistration | null> | null = null;
let alAbrir: ((url: string) => void) | null = null;

export const registrarServiceWorker = (): Promise<ServiceWorkerRegistration | null> => {
	if (registro) return registro;
	if (!("serviceWorker" in navigator)) return (registro = Promise.resolve(null));
	navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
		if (e.data && e.data.tipo === "wapp-abrir" && typeof e.data.url === "string") {
			const u = new URL(e.data.url, window.location.origin);
			const ruta = u.pathname + u.search;
			trazaAviso({ evento: "mensaje_del_sw", ruta, antes: window.location.pathname + window.location.search, cargada_hace_ms: Math.round(performance.now()) });
			// Ya se llegó: que el service worker olvide el destino guardado (si no, al volver a la app llevaría otra vez ahí).
			try { (e.source as ServiceWorker | null)?.postMessage?.({ tipo: "wapp-pendiente-listo" }); } catch { /* nada */ }
			if (alAbrir) alAbrir(ruta);
			else if (window.location.pathname + window.location.search !== ruta) window.location.assign(ruta);
		}
	});
	// Safari no siempre empieza a entregar los mensajes del service worker si solo hay addEventListener: se pide explícito.
	try { navigator.serviceWorker.startMessages(); } catch { /* navegador viejo */ }
	registro = navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).catch((e) => {
		console.warn("WAPP: no se registró el service worker", e);
		return null;
	});
	return registro;
};

/** La app (con su router) navega sola cuando tocan un aviso y WAPP ya estaba abierta. */
export const alTocarAviso = (fn: ((url: string) => void) | null) => { alAbrir = fn; };

/** Traza temporal de "tocar el aviso" (2026-10-10): qué camino funcionó en el iPhone y cuánto tardó. Va a storage/logs/avisos-traza.log. */
export const trazaAviso = (d: any) => {
	try {
		axiosInstance.post("/app/_traza", { ...d, standalone: !!(navigator as any).standalone || window.matchMedia("(display-mode: standalone)").matches,
			sw_controla: !!navigator.serviceWorker?.controller, visible: document.visibilityState }).catch(() => null);
	} catch { /* nada */ }
};

/** ¿Tocaron un aviso mientras la app dormía? El service worker contesta con "wapp-abrir" si hay destino guardado. */
export const pedirDestinoPendiente = async () => {
	// La página lee directo la caché que deja el service worker al tocar el aviso: en el iPhone los mensajes entre el
	// service worker y la página no siempre llegan (2026-10-10, Esteban: "no llevó directo"), la caché sí.
	try {
		if (typeof caches === "undefined") return;
		const c = await caches.open("wapp-pendiente");
		const r = await c.match("/__destino");
		if (!r) return;
		await c.delete("/__destino");
		const d = await r.json();
		if (!d || typeof d.url !== "string" || Date.now() - Number(d.ts || 0) > 60000) return;
		const u = new URL(d.url, window.location.origin);
		const ruta = u.pathname + u.search;
		const antes = window.location.pathname + window.location.search;
		trazaAviso({ evento: "destino_en_cache", ruta, antes, edad_ms: Date.now() - Number(d.ts || 0), sw: d, cargada_hace_ms: Math.round(performance.now()) });
		if (antes === ruta) return;
		if (alAbrir) alAbrir(ruta);
		else window.location.assign(ruta);
	} catch { /* sin caché */ }
};

// ------------------------------------------------------------------ instalar (Android / Chrome / Edge)

let promptInstalar: any = null;
const oyentesInstalar = new Set<() => void>();
const avisarInstalar = () => oyentesInstalar.forEach((f) => { try { f(); } catch { /* nada */ } });

export const escucharInstalacion = () => {
	window.addEventListener("beforeinstallprompt", (e: any) => {
		e.preventDefault();   // el botón "Instalar WAPP" lo enseña la app, cuando la persona quiera
		promptInstalar = e;
		avisarInstalar();
	});
	window.addEventListener("appinstalled", () => { promptInstalar = null; avisarInstalar(); });
};

export const puedeInstalarConBoton = (): boolean => !!promptInstalar;
export const alCambiarInstalable = (fn: () => void): (() => void) => {
	oyentesInstalar.add(fn);
	return () => { oyentesInstalar.delete(fn); };
};

export const instalar = async (): Promise<boolean> => {
	if (!promptInstalar) return false;
	const p = promptInstalar;
	promptInstalar = null;
	p.prompt();
	try {
		const r = await p.userChoice;
		avisarInstalar();
		return r && r.outcome === "accepted";
	} catch {
		return false;
	}
};

// ------------------------------------------------------------------ avisos

export type EstadoAvisos = "instalar-primero" | "no-soportado" | "bloqueados" | "inactivos" | "activos";

let llaveCache: Promise<string | null> | null = null;
const llaveServidor = (): Promise<string | null> => {
	if (!llaveCache) {
		llaveCache = axiosInstance.get("/app/_estado").then((r) => r.data?.llave || null).catch(() => { llaveCache = null; return null; });
	}
	return llaveCache;
};

const aBytes = (b64: string): Uint8Array => {
	const s = (b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
	const bin = atob(s);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
};

const mismaLlave = (sub: PushSubscription, llave: string): boolean => {
	const k = sub.options && (sub.options as any).applicationServerKey;
	if (!k) return true;   // el navegador no dice con qué llave: se asume la misma
	const a = new Uint8Array(k);
	const b = aBytes(llave);
	return a.length === b.length && a.every((v, i) => v === b[i]);
};

export const estadoAvisos = async (): Promise<EstadoAvisos> => {
	if (esIOS() && !instalada()) return "instalar-primero";
	if (!pushSoportado()) return "no-soportado";
	if (Notification.permission === "denied") return "bloqueados";
	if (Notification.permission !== "granted") return "inactivos";
	const reg = await registrarServiceWorker();
	const sub = reg ? await reg.pushManager.getSubscription() : null;
	return sub ? "activos" : "inactivos";
};

/** Lo de antes de tocar el botón (así el toque llega directo a pedir el permiso, como exige el iPhone). */
export const prepararAvisos = () => {
	if (!pushSoportado()) return;
	registrarServiceWorker();
	llaveServidor();
};

const guardar = (sub: PushSubscription) =>
	axiosInstance.post("/app/_suscripcion", { ...sub.toJSON(), dispositivo: dispositivoId(), plataforma: plataforma() });

/** "Activar Avisos": pide el permiso (debe llamarse directo desde el toque), se suscribe y lo guarda en el back. */
export const activarAvisos = async (): Promise<EstadoAvisos> => {
	if (esIOS() && !instalada()) return "instalar-primero";
	if (!pushSoportado()) return "no-soportado";
	const permiso = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
	if (permiso !== "granted") return permiso === "denied" ? "bloqueados" : "inactivos";
	const [reg, llave] = await Promise.all([registrarServiceWorker(), llaveServidor()]);
	if (!reg) throw new Error("Este navegador no dejó preparar los avisos. Recarga la página e inténtalo otra vez.");
	if (!llave) throw new Error("Los avisos todavía no están listos en el servidor.");
	let sub = await reg.pushManager.getSubscription();
	if (sub && !mismaLlave(sub, llave)) {
		await sub.unsubscribe().catch(() => null);
		sub = null;
	}
	if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: aBytes(llave) });
	await guardar(sub);
	return "activos";
};

/**
 * Al entrar con sesión: si ya había dado permiso, vuelve a guardar la suscripción (se liga a la sesión nueva) o crea
 * una si el navegador la renovó. Una vez por carga de página; nunca pide permiso.
 */
let sincronizado = false;
export const sincronizarAvisos = async () => {
	if (sincronizado || !pushSoportado() || Notification.permission !== "granted") return;
	if (esIOS() && !instalada()) return;
	sincronizado = true;
	try {
		const [reg, llave] = await Promise.all([registrarServiceWorker(), llaveServidor()]);
		if (!reg || !llave) return;
		let sub = await reg.pushManager.getSubscription();
		if (sub && !mismaLlave(sub, llave)) {
			await sub.unsubscribe().catch(() => null);
			sub = null;
		}
		if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: aBytes(llave) });
		await guardar(sub);
	} catch (e) {
		console.warn("WAPP: no se pudo renovar la suscripción de avisos", e);
	}
};

/** Al cerrar sesión: este aparato deja de recibir avisos de esta persona (máximo 1.5 s, nunca detiene la salida). */
export const olvidarEsteAparato = async () => {
	try {
		if (!pushSoportado()) return;
		const reg = await navigator.serviceWorker.getRegistration("/");
		const sub = reg ? await reg.pushManager.getSubscription() : null;
		if (!sub) return;
		await Promise.race([
			axiosInstance.post("/app/_suscripcion/_quitar", { endpoint: sub.endpoint }).catch(() => null),
			new Promise((r) => setTimeout(r, 1500)),
		]);
		await sub.unsubscribe().catch(() => null);
	} catch { /* nada */ }
};

export const probarAviso = () => axiosInstance.post("/app/_prueba").then((r) => r.data as { aparatos: number; entregados: number });

/** Aviso mostrado por la propia página (pestaña escondida). Mismo "tag" que el push: si llegan los dos, queda uno. */
export const avisoLocal = async (titulo: string, cuerpo: string, tag: string, url: string) => {
	if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
	try {
		const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration("/") : null;
		if (reg) {
			await reg.showNotification(titulo, { body: cuerpo, tag, icon: "/icons/wapp-192.png", badge: "/icons/wapp-badge-96.png", data: { url } } as any);
			return;
		}
		new Notification(titulo, { body: cuerpo, tag });
	} catch { /* sin avisos */ }
};

// ------------------------------------------------------------------ presencia

const ACTIVO_MS = 5 * 60 * 1000;    // sin tocar nada en 5 min = ya no la está viendo (se fue de la compu)
// Cada 3 min (16-oct-2026, dieta de peticiones; antes 45 s). Al volver a la pestaña se avisa una sola vez (cambio de estado).
const LATIDO_MS = 3 * 60 * 1000;

const reportar = (visible: boolean) => {
	let token: string | null = null;
	try { token = localStorage.getItem("accessToken"); } catch { /* sin almacenamiento */ }
	if (!token || !API) return;
	/*
		Petición "simple" (text/plain, token en el cuerpo, sin cabecera Authorization): al esconder o cerrar la pestaña es lo
		único que el navegador deja salir; con cabeceras pide permiso CORS antes y Chrome la aborta. El back pasa el token a
		la cabecera (App\Http\Middleware\TokenEnCuerpo).
	*/
	const cuerpo = JSON.stringify({ t: token, dispositivo: dispositivoId(), visible });
	try {
		if (!visible && navigator.sendBeacon && navigator.sendBeacon(API + "/app/_presencia", new Blob([cuerpo], { type: "text/plain;charset=UTF-8" }))) return;
		fetch(API + "/app/_presencia", { method: "POST", keepalive: true, headers: { "Content-Type": "text/plain;charset=UTF-8" }, body: cuerpo }).catch(() => null);
	} catch { /* sin red */ }
};

let presenciaIniciada = false;
export const iniciarPresencia = () => {
	if (presenciaIniciada) return;
	presenciaIniciada = true;
	let ultimaActividad = Date.now();
	let reportado: boolean | null = null;
	const aLaVista = () => document.visibilityState === "visible" && Date.now() - ultimaActividad < ACTIVO_MS;
	const evaluar = (latido = false) => {
		const v = aLaVista();
		if (v !== reportado || (v && latido)) {
			reportado = v;
			reportar(v);
		}
		// El número del ícono lo pone contadores.ts con los pendientes reales (ya no se borra al abrir).
	};
	const marcar = () => {
		ultimaActividad = Date.now();
		if (reportado === false && document.visibilityState === "visible") evaluar();
	};
	["pointerdown", "keydown", "touchstart", "wheel", "mousemove", "scroll"].forEach((ev) =>
		window.addEventListener(ev, marcar, { passive: true, capture: true }));
	document.addEventListener("visibilitychange", () => evaluar());
	window.addEventListener("pagehide", () => { reportado = false; reportar(false); });
	window.setInterval(() => evaluar(true), LATIDO_MS);
	evaluar(true);
};
